import React, { useState, useEffect } from 'react';
import { uploadImage } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './History.css';
import Spinner from './Spinner';
import { useEditMode } from '../contexts/EditModeContext';

const History = () => {
  const [title, setTitle] = useState('Historia de la Empresa');
  const [content, setContent] = useState({
    items: [] // Array unificado que mantiene el orden cronológico
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const { isEditMode } = useEditMode();

  useEffect(() => {
    const fetchHistoryInfo = async () => {
      setIsLoading(true);
      try {
        const docRef = doc(db, 'historyInfo', 'main');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title || 'Historia de la Empresa');
          
          // Migrar datos antiguos a nueva estructura si es necesario
          if (data.content && data.content.items && Array.isArray(data.content.items)) {
            setContent(data.content);
          } else if (data.items && Array.isArray(data.items)) {
            setContent(data);
          } else {
            // Migración de estructura antigua
            const items = [];
            
            // Migrar textos
            if (data.content && data.content.texts) {
              data.content.texts.forEach(text => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'text',
                  text: text.text || '',
                  headerType: text.headerType || 'h2',
                  fontType: text.fontType || 'normal',
                  color: text.color || '#f5f5dc',
                  link: text.link || '',
                  tooltip: text.tooltip || ''
                });
              });
            } else if (data.texts) {
              data.texts.forEach(text => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'text',
                  text: text.text || '',
                  headerType: text.headerType || 'h2',
                  fontType: text.fontType || 'normal',
                  color: text.color || '#f5f5dc',
                  link: text.link || '',
                  tooltip: text.tooltip || ''
                });
              });
            }
            
            // Migrar imágenes
            if (data.content && data.content.images) {
              data.content.images.forEach((image, index) => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'image',
                  url: image.url || image,
                  link: image.link || '',
                  tooltip: image.tooltip || '',
                  caption: image.caption || ''
                });
              });
            } else if (data.images) {
              data.images.forEach((image, index) => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'image',
                  url: image.url || image,
                  link: image.link || '',
                  tooltip: image.tooltip || '',
                  caption: image.caption || ''
                });
              });
            }
            
            // Migrar videos
            if (data.content && data.content.videos) {
              data.content.videos.forEach(video => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'video',
                  url: video.url || '',
                  title: video.title || '',
                  description: video.description || ''
                });
              });
            } else if (data.videos) {
              data.videos.forEach(video => {
                items.push({
                  id: Date.now() + Math.random(),
                  type: 'video',
                  url: video.url || '',
                  title: video.title || '',
                  description: video.description || ''
                });
              });
            }
            
            setContent({ items: items || [] });
          }
        } else {
          // Si no existe el documento, inicializar con estructura vacía
          setContent({ items: [] });
        }
      } catch (error) {
        console.error('Error fetching history info:', error);
        alert('Error al cargar la información histórica. Por favor, recarga la página.');
        // En caso de error, también inicializar con estructura vacía
        setContent({ items: [] });
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistoryInfo();
  }, []);

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingIndex(index);
      try {
        const imageUrl = await uploadImage(file);
        const newItems = [...content.items];
        newItems[index] = { 
          ...newItems[index],
          type: 'image', 
          url: imageUrl, 
          alt: file.name.replace(/\.[^/.]+$/, ""), // Nombre sin extensión
          uploadDate: new Date().toISOString()
        };
        setContent(prev => ({
          ...prev,
          items: newItems
        }));

        // Mostrar mensaje de éxito
        setTimeout(() => {
          alert('¡Imagen histórica subida exitosamente!');
        }, 500);

      } catch (error) {
        console.error('Error uploading image:', error);
        alert(error.message || 'Error al subir la imagen histórica. Por favor intente nuevamente.');
      } finally {
        setUploadingIndex(null);
      }
    }
  };

  const handleAddContent = (type) => {
    const newItem = {
      id: Date.now() + Math.random(),
      type: type
    };

    if (type === 'text') {
      newItem.text = '';
      newItem.headerType = 'h2';
      newItem.fontType = 'conventional';
      newItem.color = '#ffffff';
      newItem.link = '';
      newItem.tooltip = '';
    } else if (type === 'image') {
      newItem.url = '';
      newItem.link = '';
      newItem.tooltip = '';
      newItem.caption = '';
    } else if (type === 'video') {
      newItem.url = '';
      newItem.title = '';
      newItem.description = '';
    }

    setContent(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const handleRemoveContent = (index) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleVideoChange = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Extraer el ID del video de YouTube
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    return url;
  };

  const handleTextChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, text: value } : item
      )
    }));
  };

  const handleTextHeaderChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, headerType: value } : item
      )
    }));
  };

  const handleTextFontChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, fontType: value } : item
      )
    }));
  };

  const handleTextColorChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, color: value } : item
      )
    }));
  };

  const handleTextLinkChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, link: value } : item
      )
    }));
  };

  const handleTextTooltipChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, tooltip: value } : item
      )
    }));
  };

  const handleImageLinkChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, link: value } : item
      )
    }));
  };

  const handleImageTooltipChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, tooltip: value } : item
      )
    }));
  };

  const handleImageCaptionChange = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, caption: value } : item
      )
    }));
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Función helper para limpiar datos antes de guardar
  const cleanItemData = (item) => {
    const cleanItem = { ...item };
    
    // Limpiar campos de texto
    if (cleanItem.text !== undefined) {
      cleanItem.text = cleanItem.text ? cleanItem.text.trim() : '';
    }
    if (cleanItem.title !== undefined) {
      cleanItem.title = cleanItem.title ? cleanItem.title.trim() : '';
    }
    if (cleanItem.description !== undefined) {
      cleanItem.description = cleanItem.description ? cleanItem.description.trim() : '';
    }
    if (cleanItem.url !== undefined) {
      cleanItem.url = cleanItem.url || '';
    }
    if (cleanItem.link !== undefined) {
      cleanItem.link = cleanItem.link || '';
    }
    if (cleanItem.tooltip !== undefined) {
      cleanItem.tooltip = cleanItem.tooltip || '';
    }
    if (cleanItem.caption !== undefined) {
      cleanItem.caption = cleanItem.caption ? cleanItem.caption.trim() : '';
    }
    if (cleanItem.headerType !== undefined) {
      cleanItem.headerType = cleanItem.headerType || 'h2';
    }
    if (cleanItem.fontType !== undefined) {
      cleanItem.fontType = cleanItem.fontType || 'conventional';
    }
    if (cleanItem.color !== undefined) {
      cleanItem.color = cleanItem.color || '#ffffff';
    }
    
    // Asegurar que el ID existe
    if (!cleanItem.id) {
      cleanItem.id = Date.now() + Math.random();
    }
    
    // Asegurar que el tipo existe
    if (!cleanItem.type) {
      cleanItem.type = 'text';
    }
    
    return cleanItem;
  };

  const renderTextElement = (text, headerType, fontType, color, index, link, tooltip) => {
    const getFontStyle = () => {
      switch (fontType) {
        case 'elegant':
          return { fontFamily: "'Plus Jakarta Sans', sans-serif", fontStyle: 'italic' };
        case 'bold':
          return { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '700' };
        case 'italic':
          return { fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic' };
        case 'conventional':
        default:
          return { fontFamily: "'Plus Jakarta Sans', sans-serif" };
      }
    };

    const textElement = (() => {
      const baseStyle = {
        color: color || '#f5f5dc',
        ...getFontStyle()
      };
      const pStyle = { ...baseStyle, fontFamily: "'DM Sans', sans-serif" };

      switch (headerType) {
        case 'h1':
          return <h1 style={baseStyle}>{text}</h1>;
        case 'h2':
          return <h2 style={baseStyle}>{text}</h2>;
        case 'h3':
          return <h3 style={baseStyle}>{text}</h3>;
        case 'h4':
          return <h4 style={baseStyle}>{text}</h4>;
        case 'p':
          return <p style={pStyle}>{text}</p>;
        default:
          return <h2 style={baseStyle}>{text}</h2>;
      }
    })();

    if (link && isValidUrl(link)) {
      return (
        <a 
          href={link} 
          target="_blank" 
          rel="noopener noreferrer"
          title={tooltip || ''}
          style={{ 
            textDecoration: 'underline', 
            cursor: 'pointer',
            color: color || '#ffffff'
          }}
        >
          {textElement}
        </a>
      );
    }

    return textElement;
  };


  const handleSaveChanges = async () => {
    if (!content.items || content.items.length === 0) {
      alert('No puedes guardar una historia vacía. Agrega al menos un elemento.');
      return;
    }

    if (!title.trim()) {
      alert('Por favor ingresa un título para la historia.');
      return;
    }

    setIsSaving(true);
    try {
      // Limpiar y validar los datos antes de guardar
      const cleanItems = content.items.map(cleanItemData);

      await setDoc(doc(db, 'historyInfo', 'main'), {
        title: title.trim(),
        content: {
          items: cleanItems
        },
        lastUpdated: new Date().toISOString(),
        version: Date.now() // Para control de versiones
      });
      
      alert('📜 Historia guardada correctamente en los archivos de la empresa.');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error al guardar la historia. Por favor intente nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  // Función para agrupar elementos relacionados
  const groupRelatedItems = (items) => {
    if (!items || !Array.isArray(items)) return [];
    
    const groups = [];
    let currentGroup = [];
    
    items.forEach((item, index) => {
      // Los videos siempre van solos
      if (item.type === 'video') {
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
          currentGroup = [];
        }
        groups.push([item]);
      } else {
        // Para texto e imagen, intentar agruparlos
        if (currentGroup.length === 0) {
          currentGroup.push(item);
        } else if (currentGroup.length === 1) {
          const firstItem = currentGroup[0];
          // Si el primer elemento es texto y el actual es imagen, o viceversa
          if ((firstItem.type === 'text' && item.type === 'image') || 
              (firstItem.type === 'image' && item.type === 'text')) {
            currentGroup.push(item);
            groups.push(currentGroup);
            currentGroup = [];
          } else {
            // Si son del mismo tipo, agregar al grupo actual
            currentGroup.push(item);
          }
        } else {
          // Si ya hay 2 elementos en el grupo, crear un nuevo grupo
          groups.push(currentGroup);
          currentGroup = [item];
        }
      }
    });
    
    // Agregar el último grupo si no está vacío
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }
    
    return groups;
  };

  const renderHistoryItem = (item, index) => (
    <div key={`item-${item.id}`} className="history-item">
      {isEditMode && (
        <button 
          onClick={() => handleRemoveContent(index)}
          className="remove-button" 
          title={`Eliminar ${item.type === 'text' ? 'texto' : item.type === 'image' ? 'imagen' : 'video'}`}
        >
          ✕
        </button>
      )}
      
      {/* Renderizar texto */}
      {item.type === 'text' && (
        <>
          {isEditMode ? (
            <div className="text-edit-controls">
              <textarea
                value={item.text}
                onChange={(e) => handleTextChange(index, e.target.value)}
                placeholder="Escribe tu texto histórico aquí..."
                className="text-area"
              />
              <div className="text-format-controls">
                <select
                  value={item.headerType || 'h2'}
                  onChange={(e) => handleTextHeaderChange(index, e.target.value)}
                  className="text-header-select"
                >
                  <option value="h1">Título Principal (H1)</option>
                  <option value="h2">Título Secundario (H2)</option>
                  <option value="h3">Título Terciario (H3)</option>
                  <option value="p">Párrafo (P)</option>
                </select>
                <select
                  value={item.fontType || 'conventional'}
                  onChange={(e) => handleTextFontChange(index, e.target.value)}
                  className="text-font-select"
                >
                  <option value="conventional">Convencional</option>
                  <option value="elegant">Elegante</option>
                  <option value="bold">Negrita</option>
                  <option value="italic">Cursiva</option>
                </select>
                <input
                  type="color"
                  value={item.color || '#ffffff'}
                  onChange={(e) => handleTextColorChange(index, e.target.value)}
                  className="text-color-input"
                  title="Seleccionar color"
                />
              </div>
              <div className="text-link-controls">
                <input
                  type="text"
                  placeholder="URL completa (ej: https://tuapp.com/producto123)"
                  value={item.link || ''}
                  onChange={(e) => handleTextLinkChange(index, e.target.value)}
                  className={`text-link-input ${item.link && !isValidUrl(item.link) ? 'invalid-url' : ''}`}
                />
                {item.link && !isValidUrl(item.link) && (
                  <div className="text-url-error-message">
                    URL inválida
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Tooltip (texto que aparece al hacer hover)"
                  value={item.tooltip || ''}
                  onChange={(e) => handleTextTooltipChange(index, e.target.value)}
                  className="text-tooltip-input"
                />
              </div>
            </div>
          ) : (
            <div className="history-text">
              {renderTextElement(
                item.text,
                item.headerType || 'h2',
                item.fontType || 'conventional',
                item.color || '#ffffff',
                index,
                item.link || '',
                item.tooltip || ''
              )}
            </div>
          )}
        </>
      )}

      {/* Renderizar imagen */}
      {item.type === 'image' && (
        <>
          {isEditMode ? (
            <div className="image-edit-controls">
              {!item.url && (
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(index, e)}
                    className="file-input"
                    id={`file-${index}`}
                  />
                  <label htmlFor={`file-${index}`} className="file-label">
                    📁 Seleccionar Imagen Histórica
                  </label>
                </div>
              )}
              {item.url && (
                <div className="image-container">
                  <img
                    src={item.url}
                    alt="Imagen histórica"
                    className="history-image"
                    loading="lazy"
                  />
                  <div className="centered-replace-button">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(index, e)}
                      className="replace-image-input"
                      id={`replace-file-${index}`}
                      title="Reemplazar imagen"
                    />
                    <label htmlFor={`replace-file-${index}`} className="replace-image-label">
                      🔄 Reemplazar Imagen
                    </label>
                  </div>
                  <input
                    type="text"
                    placeholder="URL completa (ej: https://tuapp.com/producto123)"
                    value={item.link || ''}
                    onChange={(e) => handleImageLinkChange(index, e.target.value)}
                    className={`image-link-input ${item.link && !isValidUrl(item.link) ? 'invalid-url' : ''}`}
                  />
                  {item.link && !isValidUrl(item.link) && (
                    <div className="url-error-message">
                      URL inválida
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Tooltip (texto que aparece al hacer hover)"
                    value={item.tooltip || ''}
                    onChange={(e) => handleImageTooltipChange(index, e.target.value)}
                    className="image-tooltip-input"
                  />
                  <input
                    type="text"
                    placeholder="Pie de foto (se mostrará debajo de la imagen)"
                    value={item.caption || ''}
                    onChange={(e) => handleImageCaptionChange(index, e.target.value)}
                    className="image-caption-input"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="history-image-container">
              {item.link && isValidUrl(item.link) ? (
                <>
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    <img
                      src={item.url}
                      alt="Imagen histórica"
                      className="history-image clickable"
                      title={item.tooltip || ''}
                      loading="lazy"
                    />
                  </a>
                  {item.caption && (
                    <span className="image-caption">{item.caption}</span>
                  )}
                </>
              ) : (
                <>
                  <img
                    src={item.url}
                    alt="Imagen histórica"
                    className="history-image"
                    title={item.tooltip || ''}
                    loading="lazy"
                  />
                  {item.caption && (
                    <span className="image-caption">{item.caption}</span>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Renderizar video */}
      {item.type === 'video' && (
        <>
          {isEditMode ? (
            <div className="video-edit-controls">
              <input
                type="text"
                placeholder="URL del video de YouTube"
                value={item.url || ''}
                onChange={(e) => handleVideoChange(index, 'url', e.target.value)}
                className="video-url-input"
              />
              <input
                type="text"
                placeholder="Título del video"
                value={item.title || ''}
                onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                className="video-title-input"
              />
              <textarea
                placeholder="Descripción del video"
                value={item.description || ''}
                onChange={(e) => handleVideoChange(index, 'description', e.target.value)}
                className="video-description-input"
              />
            </div>
          ) : (
            <div className="video-container">
              {item.title && <h3 className="video-title">{item.title}</h3>}
              {item.description && <p className="video-description">{item.description}</p>}
              {item.url && (
                <div className="video-embed">
                  <iframe
                    src={getYouTubeEmbedUrl(item.url)}
                    title={item.title || "Video histórico de YouTube"}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderHistoryGroup = (group, groupIndex) => (
    <div key={`group-${groupIndex}`} className="history-item-group">
      {group.map((item, itemIndex) => {
        // Encontrar el índice real del item en el array original
        const realIndex = content.items.findIndex(originalItem => originalItem.id === item.id);
        return renderHistoryItem(item, realIndex);
      })}
    </div>
  );


  if (isLoading) {
    return (
      <div className="loading-overlay">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="history">
      {isEditMode ? (
        <div className="admin-title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="title-input"
            placeholder="Título de la historia..."
            maxLength={100}

          />
        </div>
      ) : (
        <h2>📜 {title}</h2>
      )}
      
      <div className="history-content">
        {/* Controles de administración flotantes */}
      {isEditMode && (
        <div className="floating-admin-controls-container">
          <div className="admin-controls-group">
            <div className="admin-controls">
              <button 
                onClick={() => handleAddContent('text')}
                disabled={isSaving}
                title="Agregar un nuevo párrafo a la historia"
              >
                📝 Agregar Texto
              </button>
              <button 
                onClick={() => handleAddContent('image')}
                disabled={isSaving}
                title="Agregar una imagen histórica"
              >
                🖼️ Agregar Imagen
              </button>
              <button 
                onClick={() => handleAddContent('video')}
                disabled={isSaving}
                title="Agregar un video histórico"
              >
                🎥 Agregar Video
              </button>
            </div>
            <button 
              className="floating-save-button" 
              onClick={handleSaveChanges}
              disabled={isSaving || content.length === 0}
              title={isSaving ? 'Guardando...' : 'Guardar todos los cambios'}
            >
              {isSaving ? '💾 Guardando historia...' : '💾 Guardar Historia'}
            </button>
          </div>
        </div>
      )}

            {/* Renderizar todos los elementos agrupados */}
      {content.items && Array.isArray(content.items) && groupRelatedItems(content.items).map((group, groupIndex) => 
        renderHistoryGroup(group, groupIndex)
      )}
        
      {(!content.items || content.items.length === 0) && !isEditMode && (
          <div className="empty-history">
            <p style={{ textAlign: 'center', fontSize: '1.2em', color: '#daa520', fontStyle: 'italic' }}>
              📜 La historia está esperando ser contada...
            </p>
          </div>
        )}
      </div>


    </div>
  );
};

export default History;